import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';

// Extend Window interface to include Crisp types
declare global {
  interface Window {
    $crisp?: any;
    CRISP_WEBSITE_ID?: string;
  }
}

/**
 * CrispProvider Component
 * 
 * Shows Crisp chat widget only on:
 * - LTD landing pages (/ltd/*)
 * - Pricing pages (/pricing*)
 * - Authenticated user sessions (any page when logged in)
 * 
 * Automatically sets user email and nickname in Crisp chat when user is logged in.
 */
export default function CrispProvider({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();

  // Determine if chat should be visible based on route and auth status
  const shouldShowChat = () => {
    const pathname = location.pathname;
    const isAuthenticated = !!session?.user;
    
    // Show on LTD landing pages
    if (pathname.startsWith('/ltd/')) {
      return true;
    }
    
    // Show on pricing pages
    if (pathname.startsWith('/pricing')) {
      return true;
    }
    
    // Show for authenticated users on any page
    if (isAuthenticated) {
      return true;
    }
    
    // Hide everywhere else
    return false;
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Initialize Crisp globals if not already set (script in HTML should do this, but ensure it)
    if (!window.$crisp) {
      window.$crisp = [];
    }
    if (!window.CRISP_WEBSITE_ID) {
      window.CRISP_WEBSITE_ID = "a1b564c7-a1e0-4d19-a401-ef27a4c2796f";
    }

    const showChat = shouldShowChat();
    
    // Wait for Crisp to load from the HTML script tag
    // The script in index.html should handle loading Crisp
    // We only set user data and visibility here, we don't load the script
    
    const isCrispReady = window.$crisp && typeof window.$crisp.push === 'function';
    
    if (!isCrispReady) {
      // Wait for Crisp to initialize from the HTML script
      const checkCrisp = setInterval(() => {
        if (window.$crisp && typeof window.$crisp.push === 'function') {
          console.log('[Crisp] Crisp loaded, setting visibility and user data');
          clearInterval(checkCrisp);
          setChatVisibility(showChat);
          setUserData();
        }
      }, 200);

      // Clear after 10 seconds if still not loaded
      setTimeout(() => {
        if (!window.$crisp || typeof window.$crisp.push !== 'function') {
          console.warn('[Crisp] Crisp not loaded after 10s. The script in index.html may be blocked by browser or ad blocker.');
          clearInterval(checkCrisp);
        }
      }, 10000);

      return () => clearInterval(checkCrisp);
    }

    // Crisp is ready, set visibility and user data
    setChatVisibility(showChat);
    setUserData();

    function setChatVisibility(show: boolean) {
      if (!window.$crisp || typeof window.$crisp.push !== 'function') {
        return;
      }
      
      if (show) {
        window.$crisp.push(['do', 'chat:show']);
        console.log('[Crisp] Chat widget shown');
      } else {
        window.$crisp.push(['do', 'chat:hide']);
        console.log('[Crisp] Chat widget hidden');
      }
    }

    function setUserData() {
      if (!window.$crisp) {
        console.warn('[Crisp] window.$crisp is not available');
        return;
      }
      
      console.log('[Crisp] Setting user data, session:', session?.user?.email || 'no session');

      if (session?.user) {
        // Set user email
        if (session.user.email) {
          window.$crisp.push(['set', 'user:email', [session.user.email]]);
        }

        // Set user nickname (full name from metadata, or email as fallback)
        const nickname = 
          session.user.user_metadata?.full_name || 
          session.user.user_metadata?.name ||
          session.user.email?.split('@')[0] || 
          'User';
        
        window.$crisp.push(['set', 'user:nickname', [nickname]]);

        // Optionally set user avatar if available
        if (session.user.user_metadata?.avatar_url) {
          window.$crisp.push(['set', 'user:avatar', [session.user.user_metadata.avatar_url]]);
        }

        // Note: Crisp doesn't support user:user_id namespace
        // User identification is handled automatically via email
        // If you need to store user ID, use session:data, but email is sufficient for most cases
        // Removed user_id setting to avoid errors

        // Note: We silently set user data in the background
        // Crisp will naturally prompt for email if not available
        // No automatic welcome message - let the chat flow naturally
        console.log('[Crisp] User data set silently:', {
          email: session.user.email,
          nickname,
          userId: session.user.id,
        });

        // Don't call chat:show here - let the widget appear naturally
        // The script in index.html handles widget initialization
      } else {
        // User logged out - widget remains visible and will prompt for email naturally
        console.log('[Crisp] User logged out (widget remains visible)');
        // Note: We don't call session:reset here as it can hide the widget
        // The widget will still work for anonymous users and prompt for email if needed
      }
    }
  }, [session, location.pathname]);

  return <>{children}</>;
}
